import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GraphResolver } from '../services/graphResolver';
import { SyncService } from '../services/syncService';
import { useIdentity } from '../hooks/useIdentity';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useLoginStateMachine } from '../hooks/useLoginStateMachine';
import { ErrorDisplay } from './ErrorDisplay';
import { getStoredRelays } from '../utils/relay';
import type { ServiceStatus } from '../types/identity';

export function ServiceListScreen(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { identity } = useIdentity();
  const { error, handleError, clearError } = useErrorHandler();
  const { state: loginState, dispatch } = useLoginStateMachine();
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Si on vient du flux de login, s'assurer qu'on est dans le bon état
  useEffect(() => {
    if (location.pathname === '/services' && loginState === 'S_LOGIN_SELECT_SERVICE') {
      // On est déjà dans le bon état
    } else if (location.pathname === '/services' && loginState.startsWith('S_LOGIN_')) {
      // Forcer l'état si on vient du flux login
      dispatch({ type: 'E_BACK' });
    }
  }, [location.pathname, loginState, dispatch]);

  const loadServices = async (): Promise<void> => {
    if (identity === null) {
      return;
    }

    setIsLoading(true);
    clearError();
    try {
      const relays = getStoredRelays().filter((r) => r.enabled);
      if (relays.length === 0) {
        handleError('Aucun relais activé. Synchronisez d&apos;abord.', 'NO_RELAYS');
        return;
      }

      const graphResolver = new GraphResolver();
      const syncService = new SyncService(relays, graphResolver, identity);
      await syncService.init();

      const start = identity.t0_anniversaire;
      const end = Date.now();

      await syncService.sync(start, end);

      const serviceStatuses: ServiceStatus[] = [];
      const services = graphResolver.getServices();
      for (const service of services) {
        const membres = graphResolver.getMembres();
        const membreWithService = membres.find((m) =>
          m.datajson.services_uuid.includes(service.uuid),
        );
        if (membreWithService !== undefined) {
          const path = graphResolver.resolveLoginPath(service.uuid, membreWithService.uuid);
          serviceStatuses.push({
            service_uuid: service.uuid,
            contrat_complet: path?.statut === 'complet',
            contrat_valide: path?.statut === 'complet',
            label: service.datajson.label,
          });
        } else {
          serviceStatuses.push({
            service_uuid: service.uuid,
            contrat_complet: false,
            contrat_valide: false,
            label: service.datajson.label,
          });
        }
      }

      setServices(serviceStatuses);
    } catch (err) {
      handleError(err, 'Erreur lors du chargement des services');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectService = (serviceUuid: string): void => {
    if (loginState === 'S_LOGIN_SELECT_SERVICE') {
      dispatch({ type: 'E_SELECT_SERVICE', serviceUuid });
    }
    navigate(`/select-member?service=${serviceUuid}`);
  };

  return (
    <main>
      <h1>Services disponibles</h1>
      {error !== null && <ErrorDisplay error={error} onDismiss={clearError} />}
      <section aria-labelledby="services-list">
        <h2 id="services-list">Liste des services</h2>
        {isLoading ? (
          <p>Chargement...</p>
        ) : services.length === 0 ? (
          <p>Aucun service disponible. Synchronisez d'abord.</p>
        ) : (
          <ul role="list">
            {services.map((service) => (
              <li key={service.service_uuid}>
                <div>
                  <h3>{service.label ?? service.service_uuid}</h3>
                  <p>
                    <strong>UUID:</strong> {service.service_uuid}
                  </p>
                  <p>
                    <strong>Contrat:</strong>{' '}
                    {service.contrat_complet ? 'Complet et valide' : 'Incomplet'}
                  </p>
                  <button
                    onClick={() => {
                      handleSelectService(service.service_uuid);
                    }}
                    disabled={!service.contrat_complet}
                  >
                    Se connecter
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <div>
        <button onClick={() => navigate('/')}>Retour</button>
        <button onClick={loadServices} disabled={isLoading}>
          {isLoading ? 'Chargement...' : 'Rafraîchir'}
        </button>
      </div>
    </main>
  );
}
